<template>
	<div class="relative flex justify-between items-center">
		<div class="flex py-5 gap-4">
			<LayoutCategoriesNavigation :header-mode="'default'" :menu="menu" />
		</div>
		<div>
			<NuxtLink
				:to="localePath(`/`)"
				class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			>
				<NuxtImg
					v-show="!isTransparentHeader"
					:src="siteLogo?.filename"
					:alt="siteLogo?.alt || 'logo'"
					class="logo"
					loading="eager"
					:width="siteLogoDimensions?.width"
					:height="siteLogoDimensions?.height"
				/>
				<NuxtImg
					v-show="isTransparentHeader"
					:src="siteLogoWhite?.filename"
					:alt="siteLogo?.alt || 'logo'"
					class="logo"
					loading="eager"
					:width="siteLogoDimensions?.width"
					:height="siteLogoDimensions?.height"
				/>
			</NuxtLink>
		</div>
		<div class="flex gap-3 items-center">
			<LayoutLanguageCurrency
				:cultures="culturesNorce"
				:currencies="currenciesNorce"
				:is-transparent-header="isTransparentHeader"
				class="block"
			/>
			<HeaderSearchInput
				v-if="search"
				:is-transparent-header="isTransparentHeader"
			/>
			<UserIcon class="h-6 w-6" @click="openAccountOnDesktop" />
			<div
				class="relative cursor-pointer"
				data-testid="cart-toggle"
				@click="isSidebarOpen = true"
			>
				<ShoppingBagIcon class="h-6 w-6" />
				<client-only>
					<span
						v-if="itemsCount > 0"
						data-testid="cart-badge"
						class="text-[10px] text-center absolute rounded-full w-5 h-5 leading-5 -top-2 -right-2"
						:class="
							isTransparentHeader
								? 'bg-tertiary text-primary'
								: 'bg-primary text-white'
						"
					>
						{{ itemsCount || "" }}
					</span>
				</client-only>
			</div>
		</div>

		<LayoutSideCart />
	</div>
</template>

<script setup lang="ts">
import { ShoppingBagIcon, UserIcon } from "@heroicons/vue/24/outline";
import { computed, inject, provide, ref } from "vue";
import { useRouter } from "nuxt/app";
import { storeToRefs } from "pinia";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type {
	CulturesNorce,
	SiteLogoDimensions
} from "~/types/components/header";
import type {
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft
} from "~/types/storyblok/storyblok";
import HeaderSearchInput from "../../components/HeaderSearchInput.vue";

const { userHasSession, getUserByEmailIfHasSession } = useLoadUser();

const cartStore = useCartStore();
const modal = inject<SharedModal>("modal") as SharedModal;
const { getUser } = storeToRefs(userStore());

const itemsCount = computed(() => cartStore.itemsCount());

const localePath = useLocalePath();
const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoWhite: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menu: ResolvedMenuNode[];
	isTransparentHeader?: boolean;
}

const {
	culturesNorce,
	currenciesNorce,
	search,
	siteLogo,
	siteLogoWhite,
	siteLogoDimensions,
	menu
} = defineProps<Props>();

const router = useRouter();
const openAccountOnDesktop = async () => {
	// First check if user exists in store (from localStorage persistence)
	if (getUser.value && Object.keys(getUser.value).length > 0) {
		router.push(localePath("/account"));
	} else if (!(await userHasSession())) {
		modal.open("AccountLoginForm", {
			position: "side"
		});
	} else {
		const account = await getUserByEmailIfHasSession();
		if (account) {
			router.push(localePath("/account"));
		} else {
			modal.open("AccountLoginForm", { position: "side" });
		}
	}
};
</script>
